/**
 * OrgLabelsCard — Firmenweite Bezeichnungen der Organisations-Ebenen.
 *
 * Erlaubt es, „Niederlassung" und „Standort" (Einzahl + Mehrzahl) frei zu
 * benennen (z. B. „Filiale", „Bereich", „Team"). Die Labels werden app-weit
 * in Überschriften, Filtern und Dropdowns verwendet.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { companyAPI } from '../../lib/api';
import { useOrgLabels } from '../../contexts/OrgLabelsContext';

const DEFAULTS = {
  branch_label: 'Niederlassung',
  branch_label_plural: 'Niederlassungen',
  location_label: 'Standort',
  location_label_plural: 'Standorte',
};

export const OrgLabelsCard = () => {
  const { refresh } = useOrgLabels();
  const [data, setData] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    companyAPI.getOrgLabels()
      .then((res) => setData({ ...DEFAULTS, ...(res.data || {}) }))
      .catch(() => setData(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  const setField = (key, value) => setData((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAPI.updateOrgLabels(data);
      await refresh();
      toast.success('Bezeichnungen gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setData(DEFAULTS);

  return (
    <Card data-testid="org-labels-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-primary" />
          Bezeichnungen der Organisations-Ebenen
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Passen Sie die Begriffe an Ihre Firma an. Diese Bezeichnungen erscheinen
          app-weit in Filtern, Listen und Überschriften.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Obere Ebene — Einzahl</Label>
                <Input
                  value={data.branch_label}
                  onChange={(e) => setField('branch_label', e.target.value)}
                  placeholder="Niederlassung"
                  data-testid="org-label-branch"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Obere Ebene — Mehrzahl</Label>
                <Input
                  value={data.branch_label_plural}
                  onChange={(e) => setField('branch_label_plural', e.target.value)}
                  placeholder="Niederlassungen"
                  data-testid="org-label-branch-plural"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Untere Ebene — Einzahl</Label>
                <Input
                  value={data.location_label}
                  onChange={(e) => setField('location_label', e.target.value)}
                  placeholder="Standort"
                  data-testid="org-label-location"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Untere Ebene — Mehrzahl</Label>
                <Input
                  value={data.location_label_plural}
                  onChange={(e) => setField('location_label_plural', e.target.value)}
                  placeholder="Standorte"
                  data-testid="org-label-location-plural"
                />
              </div>
            </div>
            <div
              className="rounded-lg border bg-muted/40 p-3 text-sm"
              data-testid="org-labels-preview"
            >
              <span className="text-xs font-medium text-muted-foreground">Vorschau: </span>
              <span className="font-medium">
                Firma
                <span className="text-muted-foreground mx-1.5">→</span>
                {data.branch_label_plural || 'Niederlassungen'}
                <span className="text-muted-foreground mx-1.5">→</span>
                {data.location_label_plural || 'Standorte'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleSave} disabled={saving} data-testid="org-labels-save-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Bezeichnungen speichern
              </Button>
              <Button variant="ghost" onClick={handleReset} disabled={saving} data-testid="org-labels-reset-btn">
                <RotateCcw className="mr-2 h-4 w-4" /> Auf Standardwerte zurücksetzen
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
